import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Link } from "react-router-dom";
import { Users, Megaphone, FileText, Bell, ArrowUpRight } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, sub, to, testId }) => (
  <Link to={to} data-testid={testId} className="swiss-card p-5 block fade-up">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</div>
        <div className="font-display text-3xl font-bold tracking-tight">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
      </div>
      <div className="w-9 h-9 bg-zinc-100 flex items-center justify-center" style={{borderRadius: 2}}>
        <Icon size={16} />
      </div>
    </div>
  </Link>
);

export default function Dashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) return <div className="text-zinc-500">Loading…</div>;
  const pipeline = stats.candidates;

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("overview")}</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{t("dashboard")}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-candidates" icon={Users} label={t("total_candidates")} value={pipeline.total} sub={`${pipeline.new} ${t("new").toLowerCase()}`} to="/app/candidates"/>
        <StatCard testId="stat-posts" icon={Megaphone} label={t("total_posts")} value={stats.posts.total} sub={`${stats.posts.scheduled} ${t("scheduled").toLowerCase()}`} to="/app/posts"/>
        <StatCard testId="stat-forms" icon={FileText} label={t("active_forms")} value={stats.forms_total} to="/app/forms"/>
        <StatCard testId="stat-notifs" icon={Bell} label={t("notifications")} value={stats.unread_notifications} sub={t("unread")} to="/app/notifications"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="swiss-card p-6 lg:col-span-2 fade-up">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("recent_activity")}</div>
              <div className="font-display text-xl font-bold">New candidates · 7d</div>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.chart}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#71717a' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 2, border: '1px solid #E4E4E7', fontSize: 12 }} />
                <Line type="monotone" dataKey="candidates" stroke="#002FA7" strokeWidth={2} dot={{ r: 3, fill: "#002FA7" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="swiss-card p-6 fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("pipeline")}</div>
          <div className="font-display text-xl font-bold mb-4">Candidates</div>
          <div className="space-y-3">
            {[
              { k: "new", color: "#3B82F6" },
              { k: "contacted", color: "#F59E0B" },
              { k: "qualified", color: "#8B5CF6" },
              { k: "placed", color: "#10B981" },
            ].map(({k, color}) => {
              const val = pipeline[k] || 0;
              const pct = pipeline.total ? Math.round((val / pipeline.total) * 100) : 0;
              return (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-700">{t(k)}</span>
                    <span className="font-medium">{val}</span>
                  </div>
                  <div className="h-1.5 bg-zinc-100" style={{borderRadius: 2}}>
                    <div className="h-full" style={{ width: `${pct}%`, background: color, borderRadius: 2 }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="swiss-card p-6 fade-up">
          <div className="flex justify-between items-center mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("recent_candidates")}</div>
            <Link to="/app/candidates" className="text-xs text-[#002FA7] inline-flex items-center gap-1">{t("all")} <ArrowUpRight size={12}/></Link>
          </div>
          <div className="divide-y divide-zinc-100">
            {stats.recent_candidates.length === 0 && <div className="py-6 text-zinc-500 text-sm">{t("no_data")}</div>}
            {stats.recent_candidates.map((c) => (
              <div key={c.id} className="py-3 flex items-center gap-3">
                <div className="w-9 h-9 bg-zinc-100 flex items-center justify-center font-bold text-sm" style={{borderRadius: 2}}>
                  {c.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{c.country} · {c.job_title || "—"}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5" style={{background: '#f4f4f5', borderRadius: 2}}>{t(c.status)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="swiss-card p-6 fade-up">
          <div className="flex justify-between items-center mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("notifications")}</div>
            <Link to="/app/notifications" className="text-xs text-[#002FA7] inline-flex items-center gap-1">{t("all")} <ArrowUpRight size={12}/></Link>
          </div>
          <div className="divide-y divide-zinc-100">
            {stats.recent_notifications.length === 0 && <div className="py-6 text-zinc-500 text-sm">{t("no_data")}</div>}
            {stats.recent_notifications.map((n) => (
              <div key={n.id} className="py-3 flex items-start gap-2">
                <div className={`w-1.5 h-1.5 rounded-full mt-2 ${n.read ? 'bg-zinc-300' : 'bg-[#E63946]'}`}></div>
                <div className="flex-1">
                  <div className="text-sm">{n.message}</div>
                  <div className="text-xs text-zinc-500">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
